import { useUser } from '@clerk/clerk-react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { FormInput } from '@/components/global/form-inputs/FormInput';
import { FormMultiSelect } from '@/components/global/form-inputs/FormMultiSelect';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { US_STATES } from '@/constants/territories/states';
import { VALIDATION_MESSAGES } from '@/constants/validation';
import { useCreateLead } from '@/hooks/leads/useCreateLead';
import { useCreateLeadReferral } from '@/hooks/leads/useCreateLeadReferral';
import { toast } from '@/hooks/ui/use-toast';
import useLeadsStore from '@/stores/useLeadsStore';

const AddLeadFormSchema = z.object({
  first_name: z
    .string({
      required_error: VALIDATION_MESSAGES.NAME_REQUIRED,
    })
    .min(1, { message: VALIDATION_MESSAGES.NAME_REQUIRED }),
  last_name: z.string().optional(),
  email: z
    .string({
      required_error: VALIDATION_MESSAGES.EMAIL_REQUIRED,
    })
    .min(1, { message: VALIDATION_MESSAGES.EMAIL_REQUIRED })
    .email({ message: VALIDATION_MESSAGES.EMAIL_INVALID }),
  preferred_markets: z.array(z.string()).optional(),
  phone: z.string().optional(),
});

interface AddLeadFormProps {
  closeDialog: () => void;
  onCreateLeadError?: (message: string) => void;
  onClearCreateLeadError?: () => void;
}

type CreateLeadErrorPayload = {
  message?: string;
  error?: {
    message?: string;
    details?: unknown;
  };
  response?: {
    data?: {
      message?: string;
      error?: {
        message?: string;
        details?: unknown;
      };
    };
  };
};

const getCreateLeadErrorMessage = (error: unknown): string => {
  const knownError = error as CreateLeadErrorPayload;
  const payload = knownError?.response?.data ?? knownError;

  if (typeof payload?.error?.message === 'string' && payload.error.message) {
    return payload.error.message;
  }

  if (typeof payload?.message === 'string' && payload.message) {
    return payload.message;
  }

  if (typeof payload?.error?.details === 'string' && payload.error.details) {
    return payload.error.details;
  }

  return 'Please try again.';
};

const AddLeadForm = ({
  closeDialog,
  onCreateLeadError,
  onClearCreateLeadError,
}: AddLeadFormProps) => {
  const navigate = useNavigate();
  const { user } = useUser();
  const { mutate: createLead } = useCreateLead();
  const { mutate: createLeadReferral } = useCreateLeadReferral();
  const { triggerRefresh } = useLeadsStore();
  const [isCreatingLead, setIsCreatingLead] = useState(false);

  const form = useForm<z.infer<typeof AddLeadFormSchema>>({
    resolver: zodResolver(AddLeadFormSchema),
    defaultValues: {},
  });

  const onSubmit = async (values: z.infer<typeof AddLeadFormSchema>) => {
    onClearCreateLeadError?.();

    if (!user?.id) {
      toast({
        variant: 'destructive',
        title: 'Something went wrong',
        description: 'User information is not available.',
      });
      return;
    }

    setIsCreatingLead(true);

    try {
      const leadResult = await createLead({
        first_name: values.first_name,
        last_name: values.last_name || undefined,
        email: values.email,
        phone: values.phone || undefined,
        preferred_markets: values.preferred_markets,
        status: 'REFERRED',
      });

      if (leadResult?.data?.id) {
        onClearCreateLeadError?.();
        let referralLinked = false;

        try {
          await createLeadReferral({
            referred_user_id: leadResult.data.id,
          });
          referralLinked = true;

          toast({
            title: 'Success',
            description: 'Lead created successfully',
          });
        } catch (referralError) {
          console.error('Failed to create lead referral', referralError);
          toast({
            variant: 'destructive',
            title: 'Partial Success',
            description:
              'Lead created successfully, but failed to link referral. Please contact support if needed.',
          });
        }

        if (referralLinked) {
          triggerRefresh();
          closeDialog();
          navigate(
            `/dashboard/lead-profile/${leadResult.data.id}?addNotes=true`
          );
          return;
        }

        closeDialog();
      }
    } catch (error) {
      console.error('Failed to create lead', error);
      const errorMessage = getCreateLeadErrorMessage(error);
      onCreateLeadError?.(errorMessage);
      toast({
        variant: 'destructive',
        title: 'Failed to create lead',
        description: errorMessage,
      });
    } finally {
      setIsCreatingLead(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="w-full">
        <div className="space-y-4 p-5">
          <div className="grid grid-cols-2 gap-4">
            <FormInput
              type="text"
              name="first_name"
              label="First Name"
              control={form.control}
              placeholder="Enter first name"
            />

            <FormInput
              type="text"
              name="last_name"
              label="Last Name"
              control={form.control}
              placeholder="Enter last name"
            />
          </div>

          <FormInput
            type="text"
            name="email"
            label="Email"
            control={form.control}
            placeholder="example@gmail.com"
          />

          <FormInput
            type="phone"
            name="phone"
            label="Phone"
            control={form.control}
            placeholder="(603) 555-0123"
          />

          <div className="flex flex-col items-start gap-2">
            <div className="as-title-03">
              Preferred Market
              <p className="as-title-04">You can select 5 max.</p>
            </div>
            <FormMultiSelect
              name="preferred_markets"
              control={form.control}
              options={Object.entries(US_STATES).map(([key, value]) => ({
                value: key,
                label: value,
              }))}
              className="w-full"
              maxSelections={5}
              placeholder="Select up to 5 markets"
            />
          </div>
        </div>
        <div className="flex justify-between border-t p-4">
          <Button
            type="button"
            variant="outline"
            onClick={closeDialog}
            disabled={isCreatingLead}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={isCreatingLead}>
            Add Lead
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default AddLeadForm;
